/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { useIntl } from 'react-intl'

import { useIsDarkMode } from '@elastic/eui'

import type { OrganizationOption } from '@modules/organization-api'

// eslint-disable-next-line import/no-restricted-paths
import gcpLogo from '@/files/GCP.svg'
// eslint-disable-next-line import/no-restricted-paths
import azureLogo from '@/files/Azure.svg'
// eslint-disable-next-line import/no-restricted-paths
import awsLogo from '@/files/AWS.svg'
// eslint-disable-next-line import/no-restricted-paths
import awsLogoDark from '@/files/AWS-dark.svg'

import { messages } from './messages'

export const useMarketplaceIcons = () => {
  const { formatMessage } = useIntl()
  const isDarkMode = useIsDarkMode()
  return {
    aws: {
      icon: isDarkMode ? awsLogoDark : awsLogo,
      name: formatMessage(messages.awsMarketplace),
    },
    azure: {
      icon: azureLogo,
      name: formatMessage(messages.azureMarketplace),
    },
    gcp: {
      icon: gcpLogo,
      name: formatMessage(messages.gcpMarketplace),
    },
  }
}

/**
 * Sorts organization memberships alphabetically, with the current organization first
 * @param memberships - Array of organization memberships
 * @param currentOrgId - ID of the current organization to place first
 * @returns Sorted array with current org first, others alphabetically
 */
export const sortMemberships = ({
  memberships,
  currentOrgId,
}: {
  memberships: OrganizationOption[]
  currentOrgId: string | undefined
}) =>
  [...memberships].sort((a, b) => {
    if (a.organization_id === currentOrgId) {
      return -1
    }

    if (b.organization_id === currentOrgId) {
      return 1
    }

    return a.organization_name.toLowerCase().localeCompare(b.organization_name.toLowerCase())
  })
